import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const FireLifeSafety = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{translations['fire.title']} | Risk Consulting | Fepsec</title>
        <meta name="description" content={translations['fire.meta.description']} />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-700 to-orange-600 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-aos="fade-up">{translations['fire.title']}</h1>
            <p className="text-xl text-orange-100 mb-8" data-aos="fade-up" data-aos-delay="100">
              {translations['fire.hero.subtitle']}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['fire.intro.title']}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['fire.intro.p1']}
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['fire.intro.p2']}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="100">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-blue-100 mb-4">{translations['fire.approach.title']}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.approach.item1']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.approach.item2']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.approach.item3']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.approach.item4']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.approach.item5']}</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="200">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-blue-100 mb-4">{translations['fire.benefits.title']}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.benefits.item1']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.benefits.item2']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.benefits.item3']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.benefits.item4']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>{translations['fire.benefits.item5']}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['fire.services.title']}</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-3">{translations['fire.services.assessment.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['fire.services.assessment.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-3">{translations['fire.services.protection.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['fire.services.protection.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-3">{translations['fire.services.emergency.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['fire.services.emergency.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-3">{translations['fire.services.training.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['fire.services.training.description']}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['fire.cta.title']}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8">
              {translations['fire.cta.description']}
            </p>
            <a 
              href="/contact-us" 
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
            >
              {translations['fire.cta.button']}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FireLifeSafety;